import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import { useAuth } from "../hooks/helper/useAuth";
import Loading from "../components/Loading";
import { IViewAsPayload } from "../interfaces";
import brandLogo from "../assets/images/Logo.png";

const TakeOverLogin: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { takeOverLogin, isLoading: authLoading } = useAuth();

  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const token = searchParams.get("token");
  const accessToken = searchParams.get("accessToken");

  useEffect(() => {
    const handleTakeOverLogin = async () => {
      // If no token is provided, show error
      if (!token) {
        setError("No token provided");
        return;
      }

      if (!accessToken) {
        setError("No access token provided");
        return;
      }

      // If auth is already loading, wait
      if (authLoading) {
        return;
      }

      setIsProcessing(true);
      setError(null);

      try {
        // Create a payload with the token
        // Note: The takeOverLogin function expects ILoginPayload but we're using token
        // You may need to adjust this based on your API requirements
        const payload: IViewAsPayload = {
          encryptedEmail: String(token.replace(/ /g, "+")) || "",
          accessToken: String(accessToken.replace(/ /g, "+")) || "", // Using token as password for take over login
        };

        await takeOverLogin(payload);

        // Success - redirect to dashboard or home
        toast.success("Login successful");
        navigate("/dashboard");
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Take over login failed";
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        setIsProcessing(false);
      }
    };

    handleTakeOverLogin();
  }, [token, takeOverLogin, authLoading, navigate, accessToken]);

  // Show loading state while processing or if auth is loading
  if (isProcessing || authLoading) {
    return <Loading />;
  }

  // Show error state if no token or other error
  if (error || !token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-200 via-indigo-100 to-white relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/10 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))]"></div>
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-200/20 via-indigo-100/20 to-white/20"></div>

        <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center mb-8 animate-fade-in">
            <img src={brandLogo} alt="Logo" className="h-16 mb-4" />
          </div>

          <div className="w-full relative z-10 shadow-2xl border-none bg-white/90 backdrop-blur-sm animate-fade-in-up rounded-lg p-8">
            <div className="text-center">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                {!token ? "Invalid Token" : "Login Failed"}
              </h2>
              <p className="mt-2 text-sm text-gray-600 mb-6">
                {!token
                  ? "No token was provided in the URL. Please check your link and try again."
                  : error ||
                    "An error occurred during login. Please try again."}
              </p>
              <button
                onClick={() => navigate("/auth")}
                className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                Go to Login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // This should never be reached, but just in case
  return <Loading />;
};

export default TakeOverLogin;
